/**
 * Random number generator matching original C++ implementation
 * Reference: quadra/source/random.cc:36-42
 */
export default class Random {
  private seed: number;

  constructor(seed?: number) {
    this.seed = seed ?? Date.now();
  }

  get_seed(): number {
    return this.seed;
  }

  set_seed(seed: number): void {
    this.seed = seed;
  }

  /**
   * Generate random number matching original algorithm
   * @param _and Bitmask (default 0xFFFF for 16-bit)
   * @returns Random number & _and
   * 
   * Original C++ implementation:
   *   seed = seed * 0x41c64e6d + 0x00003039;  // signed integer overflow
   *   tmp = int(seed >> 10);                  // arithmetic right shift (signed)
   *   return static_cast<uint16_t>(tmp & _and);
   * 
   * Key insight: Even though C++ uses signed arithmetic, the final cast to uint16_t
   * means we only use the lower 16 bits. The critical part is ensuring 32-bit overflow
   * behavior matches. We use | 0 to force 32-bit arithmetic in JavaScript.
   */
  rnd(_and: number = 0xFFFF): number {
    // Force 32-bit arithmetic: | 0 converts to signed 32-bit, preserving overflow behavior
    // This matches C++ signed integer overflow: seed * 0x41c64e6d + 0x00003039
    const newSeed = ((this.seed | 0) * 0x41c64e6d + 0x00003039) | 0;
    // Store as unsigned 32-bit (preserves the bit pattern from signed overflow)
    this.seed = newSeed >>> 0;
    // Right shift: C++ uses arithmetic shift on signed, but since we cast to uint16_t,
    // we only care about lower 16 bits. Logical shift >>> gives same lower bits.
    const tmp = this.seed >>> 10;
    // Return lower 16 bits (matches static_cast<uint16_t>)
    return (tmp & _and) & 0xFFFF;
  }
}

